import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { GripVertical, Plus, X, ChevronDown } from "lucide-react";
import {
  fetchDeliveryCostConfig,
  putDeliveryCostConfig,
  previewDeliveryCostConfig,
  fetchTrackHubCarriers,
} from "../../services/trackHubApi";
import "./trackhub-delivery-costs.css";

const TIER_TYPES = [
  { id: "percent_of_cost", i18n: "typePercentOfCost" },
  { id: "fixed_discount", i18n: "typeFixedDiscount" },
  { id: "free", i18n: "typeFree" },
];

function newTierId() {
  return `tmp_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
}

function emptyTier() {
  return {
    clientId: newTierId(),
    fromAmount: 0,
    toAmount: "",
    type: "percent_of_cost",
    value: 0,
    carrierScope: "all",
    carrierIds: [],
  };
}

function normalizeLoadedTiers(tiers) {
  return (tiers || []).map((t) => ({
    clientId: t.id || newTierId(),
    id: t.id,
    fromAmount: t.fromAmount ?? 0,
    toAmount: t.toAmount == null ? "" : t.toAmount,
    type: t.type || "percent_of_cost",
    value: t.type === "free" ? 0 : (t.value ?? 0),
    carrierScope: t.carrierScope || "all",
    carrierIds: Array.isArray(t.carrierIds) ? t.carrierIds : [],
  }));
}

function toPayloadTiers(tiers) {
  return tiers.map((t, index) => {
    const toRaw = t.toAmount;
    const toAmount =
      toRaw === "" || toRaw == null || toRaw === "∞" ? null : Number(toRaw);
    const carrierScope = t.carrierScope === "specific" ? "specific" : "all";
    return {
      ...(t.id ? { id: t.id } : {}),
      order: index + 1,
      fromAmount: Number(t.fromAmount) || 0,
      toAmount: Number.isFinite(toAmount) ? toAmount : null,
      type: t.type || "percent_of_cost",
      value: t.type === "free" ? 0 : Number(t.value) || 0,
      carrierScope,
      carrierIds:
        carrierScope === "specific" && t.carrierIds?.[0]
          ? [String(t.carrierIds[0]).toUpperCase()]
          : [],
    };
  });
}

function parseToAmount(toRaw) {
  if (toRaw === "" || toRaw == null || toRaw === "∞") return null;
  const n = Number(toRaw);
  return Number.isFinite(n) ? n : null;
}

/** Per-field inline errors for a tier row. */
function getTierFieldErrors(tier, t, dc) {
  const errors = {};
  const from = Number(tier.fromAmount);
  const to = parseToAmount(tier.toAmount);
  const value = Number(tier.value);
  const toLabel = to != null ? String(to) : "∞";

  if (Number.isFinite(from) && to != null && from > to) {
    const msg = t(`${dc}.errFromGreaterThanTo`, { to: toLabel });
    errors.fromAmount = msg;
    errors.toAmount = msg;
  }

  if (tier.type === "percent_of_cost") {
    if (Number.isFinite(value) && value > 100) {
      errors.value = t(`${dc}.errPercentMax`, { max: 100 });
    }
  }

  if (
    tier.type === "fixed_discount"
    && to != null
    && Number.isFinite(value)
    && value > to
  ) {
    errors.value = t(`${dc}.errFixedExceedsTo`, { to: toLabel });
  }

  if (tier.type !== "free" && Number.isFinite(value) && value < 0) {
    errors.value = t(`${dc}.errValueNegative`);
  }

  return errors;
}

/** Clamp Value so it stays within type/To limits. */
function clampTierValue(tier) {
  if (tier.type === "free") return { ...tier, value: 0 };
  const value = Number(tier.value);
  if (!Number.isFinite(value)) return tier;

  let next = value;
  if (next < 0) next = 0;

  if (tier.type === "percent_of_cost" && next > 100) {
    next = 100;
  }

  if (tier.type === "fixed_discount") {
    const to = parseToAmount(tier.toAmount);
    if (to != null && next > to) next = to;
  }

  if (next === value) return tier;
  return { ...tier, value: next };
}

function tierValueNeedsClamp(tier) {
  const clamped = clampTierValue(tier);
  return Number(clamped.value) !== Number(tier.value);
}

const VALUE_AUTO_CORRECT_MS = 1200;

function formatMatchedLabel(tier, t) {
  if (!tier) return t("trackHubBranchesPage.deliveryCosts.noMatch");
  const from = tier.fromAmount ?? 0;
  const to = tier.toAmount == null ? "∞" : tier.toAmount;
  let suffix = "";
  if (tier.type === "free") suffix = t("trackHubBranchesPage.deliveryCosts.freeShort");
  else if (tier.type === "fixed_discount") suffix = `−${tier.value}`;
  else suffix = `${tier.value}%`;
  return `${from}→${to} → ${suffix}`;
}

function formatMoney(n) {
  return Number(n ?? 0).toFixed(2);
}

function TrackHubDeliveryCostsTab({ readOnly = false }) {
  const { t, i18n } = useTranslation("translation");
  const isRtl = i18n.language === "ar" || i18n.dir() === "rtl";
  const dc = "trackHubBranchesPage.deliveryCosts";

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [tiers, setTiers] = useState([emptyTier()]);
  const [carriers, setCarriers] = useState([]);
  const [dirty, setDirty] = useState(false);

  const [exampleValue, setExampleValue] = useState("80");
  const [exampleQuote, setExampleQuote] = useState("16");
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  const [dragIndex, setDragIndex] = useState(null);

  const tierErrors = useMemo(
    () => tiers.map((tier) => getTierFieldErrors(tier, t, dc)),
    [tiers, t]
  );
  const hasTierErrors = useMemo(
    () => tierErrors.some((e) => Object.keys(e).length > 0),
    [tierErrors]
  );

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [cfgRes, carriersRes] = await Promise.all([
        fetchDeliveryCostConfig(),
        fetchTrackHubCarriers({ refresh: false }),
      ]);
      const loaded = normalizeLoadedTiers(cfgRes?.data?.tiers);
      setTiers(loaded.length ? loaded : [emptyTier()]);
      setCarriers((carriersRes?.data || []).filter((c) => c.isActive !== false));
      setDirty(false);
    } catch (error) {
      console.error("Failed to load delivery cost config", error);
      toast.error(t(`${dc}.loadError`));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    load();
  }, [load]);

  // Show error + highlight first, then auto-correct Value after a short pause.
  useEffect(() => {
    if (loading) return;
    if (!tiers.some(tierValueNeedsClamp)) return;

    const handle = setTimeout(() => {
      setTiers((prev) => {
        let changed = false;
        const next = prev.map((tier) => {
          if (!tierValueNeedsClamp(tier)) return tier;
          changed = true;
          return clampTierValue(tier);
        });
        return changed ? next : prev;
      });
      setDirty(true);
    }, VALUE_AUTO_CORRECT_MS);

    return () => clearTimeout(handle);
  }, [tiers, loading]);

  const updateTier = useCallback((updater) => {
    setTiers((prev) => {
      const next = typeof updater === "function" ? updater(prev) : updater;
      return next;
    });
    setDirty(true);
  }, []);

  const handleField = useCallback(
    (index, field, value) => {
      updateTier((prev) =>
        prev.map((row, i) => {
          if (i !== index) return row;
          const next = { ...row, [field]: value };
          if (field === "type" && value === "free") next.value = 0;
          if (field === "carrierScope" && value === "all") next.carrierIds = [];
          return next;
        })
      );
    },
    [updateTier]
  );

  const handleCarrierSelect = useCallback(
    (index, carrierId) => {
      updateTier((prev) =>
        prev.map((row, i) => {
          if (i !== index) return row;
          if (!carrierId || carrierId === "all") {
            return { ...row, carrierScope: "all", carrierIds: [] };
          }
          return { ...row, carrierScope: "specific", carrierIds: [carrierId] };
        })
      );
    },
    [updateTier]
  );

  const addTier = useCallback(() => {
    updateTier((prev) => [...prev, emptyTier()]);
  }, [updateTier]);

  const removeTier = useCallback(
    (index) => {
      updateTier((prev) => {
        if (prev.length <= 1) return [emptyTier()];
        return prev.filter((_, i) => i !== index);
      });
    },
    [updateTier]
  );

  const onDragStart = (index) => setDragIndex(index);
  const onDragOver = (e, index) => {
    e.preventDefault();
    if (dragIndex == null || dragIndex === index) return;
    updateTier((prev) => {
      const next = [...prev];
      const [moved] = next.splice(dragIndex, 1);
      next.splice(index, 0, moved);
      return next;
    });
    setDragIndex(index);
  };
  const onDragEnd = () => setDragIndex(null);

  const handleSave = useCallback(async () => {
    if (hasTierErrors) {
      toast.error(t(`${dc}.fixErrors`));
      return;
    }
    setSaving(true);
    try {
      const payload = { tiers: toPayloadTiers(tiers) };
      const res = await putDeliveryCostConfig(payload);
      const loaded = normalizeLoadedTiers(res?.data?.tiers);
      setTiers(loaded.length ? loaded : [emptyTier()]);
      setDirty(false);
      toast.success(t(`${dc}.saveSuccess`));
    } catch (error) {
      const message = error?.response?.data?.message || t(`${dc}.saveError`);
      toast.error(message);
    } finally {
      setSaving(false);
    }
  }, [tiers, t, hasTierErrors]);

  const runPreview = useCallback(async () => {
    if (hasTierErrors) {
      setPreview(null);
      return;
    }
    const shipmentValue = Number(exampleValue);
    const carrierQuote = Number(exampleQuote);
    if (!Number.isFinite(shipmentValue) || shipmentValue < 0) {
      return;
    }
    if (!Number.isFinite(carrierQuote) || carrierQuote < 0) {
      return;
    }
    setPreviewLoading(true);
    try {
      const res = await previewDeliveryCostConfig({
        shipmentValue,
        carrierQuote,
        tiers: toPayloadTiers(tiers),
      });
      setPreview(res?.data || null);
    } catch (error) {
      const message = error?.response?.data?.message || t(`${dc}.previewError`);
      toast.error(message);
    } finally {
      setPreviewLoading(false);
    }
  }, [exampleValue, exampleQuote, tiers, t, hasTierErrors]);

  useEffect(() => {
    if (loading) return;
    const handle = setTimeout(() => {
      runPreview();
    }, 350);
    return () => clearTimeout(handle);
  }, [loading, tiers, exampleValue, exampleQuote, runPreview]);

  const carrierOptions = useMemo(
    () => [
      { id: "all", label: t(`${dc}.carrierAll`) },
      ...carriers.map((c) => ({
        id: c.carrierId || c.id,
        label: c.name || c.carrierId || c.id,
      })),
    ],
    [carriers, t]
  );

  const carrierFeeNum = Number(preview?.carrierFee ?? preview?.total ?? exampleQuote) || 0;
  const youPayNum = Number(preview?.youPay ?? 0) || 0;
  const customerPaysNum = Number(
    preview?.customerPays ?? Math.max(0, carrierFeeNum - youPayNum)
  );

  const youPayDisplay = previewLoading ? "…" : formatMoney(youPayNum);
  const customerPaysDisplay = previewLoading ? "…" : formatMoney(customerPaysNum);

  if (loading) {
    return (
      <div className="trackhub-delivery-costs-tab">
        <div className="trackhub-dc-panel trackhub-dc-panel--loading">
          {t(`${dc}.loading`)}
        </div>
      </div>
    );
  }

  return (
    <div className="trackhub-delivery-costs-tab" dir={isRtl ? "rtl" : "ltr"}>
      <fieldset
        disabled={readOnly}
        className="trackhub-dc-panel"
        style={{ border: 0, margin: 0, padding: 0, minWidth: 0 }}
      >
        <div className="trackhub-dc-panel__header">
          <h3 className="trackhub-dc-panel__title">{t(`${dc}.rulesTitle`)}</h3>
        </div>

        <div className="trackhub-dc-panel__body">
          <div className="trackhub-dc-section">
            <p className="trackhub-dc-section__title">{t(`${dc}.tiersTitle`)}</p>
            <p className="trackhub-dc-section__hint">{t(`${dc}.tiersHint`)}</p>

            <div className="trackhub-dc-tiers">
              {tiers.map((tier, index) => {
                const carrierSelectValue =
                  tier.carrierScope === "specific" && tier.carrierIds?.[0]
                    ? tier.carrierIds[0]
                    : "all";
                const toIsEmpty = tier.toAmount === "" || tier.toAmount == null;
                const toAmountNum = parseToAmount(tier.toAmount);
                const errors = tierErrors[index] || {};
                return (
                  <div
                    key={tier.clientId}
                    className={[
                      "trackhub-dc-tier-row",
                      dragIndex === index ? "is-dragging" : "",
                    ]
                      .filter(Boolean)
                      .join(" ")}
                    onDragOver={(e) => onDragOver(e, index)}
                    onDragEnd={onDragEnd}
                  >
                    <div className="trackhub-dc-tier-row__aside">
                      <span
                        className="trackhub-dc-tier-row__grip"
                        draggable
                        onDragStart={() => onDragStart(index)}
                        aria-label={t(`${dc}.reorderTier`)}
                        title={t(`${dc}.reorderTier`)}
                      >
                        <GripVertical size={14} strokeWidth={2} />
                      </span>
                      <span className="trackhub-dc-tier-row__badge">{index + 1}</span>
                    </div>

                    <div className="trackhub-dc-tier-card">
                      <label className="trackhub-dc-field trackhub-dc-field--from">
                        <span className="trackhub-dc-field__label">{t(`${dc}.from`)}</span>
                        <input
                          type="number"
                          min={0}
                          step="any"
                          className={[
                            "trackhub-dc-input",
                            errors.fromAmount ? "trackhub-dc-input--error" : "",
                          ]
                            .filter(Boolean)
                            .join(" ")}
                          value={tier.fromAmount}
                          onChange={(e) => handleField(index, "fromAmount", e.target.value)}
                          aria-invalid={Boolean(errors.fromAmount)}
                        />
                        {errors.fromAmount ? (
                          <span className="trackhub-dc-field__error">{errors.fromAmount}</span>
                        ) : null}
                      </label>

                      <label className="trackhub-dc-field trackhub-dc-field--to">
                        <span className="trackhub-dc-field__label">{t(`${dc}.to`)}</span>
                        <input
                          type="text"
                          inputMode="decimal"
                          className={[
                            "trackhub-dc-input",
                            toIsEmpty ? "trackhub-dc-input--infinity" : "",
                            errors.toAmount ? "trackhub-dc-input--error" : "",
                          ]
                            .filter(Boolean)
                            .join(" ")}
                          placeholder="∞"
                          value={toIsEmpty ? "∞" : tier.toAmount}
                          onFocus={(e) => {
                            if (toIsEmpty) e.target.select();
                          }}
                          onChange={(e) => {
                            const raw = e.target.value;
                            if (raw === "" || raw === "∞") {
                              handleField(index, "toAmount", "");
                              return;
                            }
                            handleField(index, "toAmount", raw.replace(/[^\d.]/g, ""));
                          }}
                          aria-invalid={Boolean(errors.toAmount)}
                        />
                        {errors.toAmount ? (
                          <span className="trackhub-dc-field__error">{errors.toAmount}</span>
                        ) : null}
                      </label>

                      <label className="trackhub-dc-field trackhub-dc-field--grow">
                        <span className="trackhub-dc-field__label">{t(`${dc}.type`)}</span>
                        <div className="trackhub-dc-select-wrap">
                          <select
                            className="trackhub-dc-input trackhub-dc-select"
                            value={tier.type}
                            onChange={(e) => handleField(index, "type", e.target.value)}
                          >
                            {TIER_TYPES.map((opt) => (
                              <option key={opt.id} value={opt.id}>
                                {t(`${dc}.${opt.i18n}`)}
                              </option>
                            ))}
                          </select>
                          <ChevronDown size={11} className="trackhub-dc-select-wrap__icon" />
                        </div>
                      </label>

                      <label className="trackhub-dc-field trackhub-dc-field--grow">
                        <span className="trackhub-dc-field__label">{t(`${dc}.value`)}</span>
                        <input
                          type="number"
                          min={0}
                          max={
                            tier.type === "percent_of_cost"
                              ? 100
                              : tier.type === "fixed_discount" && toAmountNum != null
                                ? toAmountNum
                                : undefined
                          }
                          step="any"
                          className={[
                            "trackhub-dc-input",
                            errors.value ? "trackhub-dc-input--error" : "",
                          ]
                            .filter(Boolean)
                            .join(" ")}
                          disabled={tier.type === "free"}
                          value={tier.type === "free" ? 0 : tier.value}
                          onChange={(e) => handleField(index, "value", e.target.value)}
                          aria-invalid={Boolean(errors.value)}
                        />
                        {errors.value ? (
                          <span className="trackhub-dc-field__error">{errors.value}</span>
                        ) : null}
                      </label>

                      <label className="trackhub-dc-field trackhub-dc-field--grow">
                        <span className="trackhub-dc-field__label">{t(`${dc}.carrier`)}</span>
                        <div className="trackhub-dc-select-wrap">
                          <select
                            className={[
                              "trackhub-dc-input",
                              "trackhub-dc-select",
                              carrierSelectValue === "all" ? "trackhub-dc-input--muted-value" : "",
                            ]
                              .filter(Boolean)
                              .join(" ")}
                            value={carrierSelectValue}
                            onChange={(e) => handleCarrierSelect(index, e.target.value)}
                          >
                            {carrierOptions.map((opt) => (
                              <option key={opt.id} value={opt.id}>
                                {opt.label}
                              </option>
                            ))}
                          </select>
                          <ChevronDown size={11} className="trackhub-dc-select-wrap__icon" />
                        </div>
                      </label>

                      <div className="trackhub-dc-tier-card__actions">
                        <button
                          type="button"
                          className="trackhub-dc-icon-btn"
                          title={t(`${dc}.deleteTier`)}
                          aria-label={t(`${dc}.deleteTier`)}
                          onClick={() => removeTier(index)}
                        >
                          <X size={13} strokeWidth={2} />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <button type="button" className="trackhub-dc-btn trackhub-dc-btn--ghost" onClick={addTier}>
              <Plus size={13} strokeWidth={2.5} />
              {t(`${dc}.addTier`)}
            </button>
          </div>

          <div className="trackhub-dc-example-wrap">
            <div className="trackhub-dc-example">
            <p className="trackhub-dc-example__title">{t(`${dc}.tryExample`)}</p>
            <p className="trackhub-dc-example__hint">{t(`${dc}.tryExampleHint`)}</p>

            <div className="trackhub-dc-example__inputs">
              <label className="trackhub-dc-field">
                <span className="trackhub-dc-field__label trackhub-dc-field__label--muted">
                  {t(`${dc}.orderValue`)}
                </span>
                <input
                  type="number"
                  min={0}
                  step="any"
                  className="trackhub-dc-input trackhub-dc-input--lg"
                  value={exampleValue}
                  onChange={(e) => setExampleValue(e.target.value)}
                />
              </label>
              <label className="trackhub-dc-field">
                <span className="trackhub-dc-field__label trackhub-dc-field__label--muted">
                  {t(`${dc}.carrierQuote`)}
                </span>
                <input
                  type="number"
                  min={0}
                  step="any"
                  className="trackhub-dc-input trackhub-dc-input--lg"
                  value={exampleQuote}
                  onChange={(e) => setExampleQuote(e.target.value)}
                />
              </label>
            </div>

            <div className="trackhub-dc-example__results">
              <div className="trackhub-dc-example__row">
                <span>{t(`${dc}.matchedTier`)}</span>
                <span className="trackhub-dc-example__mono">
                  {previewLoading
                    ? "…"
                    : hasTierErrors
                      ? "—"
                      : formatMatchedLabel(preview?.matchedTier, t)}
                </span>
              </div>
              <div className="trackhub-dc-example__row">
                <span>{t(`${dc}.youPay`)}</span>
                <span className="trackhub-dc-example__strong">
                  {hasTierErrors ? "—" : youPayDisplay}
                </span>
              </div>
              <div className="trackhub-dc-example__row">
                <span>{t(`${dc}.customerPays`)}</span>
                <span className="trackhub-dc-example__customer">
                  {hasTierErrors ? "—" : customerPaysDisplay}
                </span>
              </div>
            </div>
            </div>
          </div>
        </div>

        <div className="trackhub-dc-panel__footer">
          <button
            type="button"
            className="trackhub-dc-btn trackhub-dc-btn--primary"
            disabled={saving || !dirty || hasTierErrors}
            onClick={handleSave}
          >
            {saving ? t(`${dc}.saving`) : t(`${dc}.save`)}
          </button>
        </div>
      </fieldset>
    </div>
  );
}

export default TrackHubDeliveryCostsTab;
